import clsx from "clsx";
import { TriangleAlert } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import { Link } from "wouter";
import { DraftFields, SelectField, ToggleField } from "../../web/components/fields";
import { FixHere } from "../../web/components/FixHere";
import { DraftBar, ErrorNotice, SectionHeading } from "../../web/components/ui";
import type { DiffPart } from "../../web/lib/diff";
import { can, groupNameCheck, usePriceListSaver } from "./data";
import { GroupCardHeader } from "./GroupCardHeader";
import { type MaterialRow, meta, type PriceListData } from "./meta";
import { applyTags, type ChoiceTag, differingTags, type GroupTags, type IncludeTag, groupTagsName, itemTags, liveGroupTags, type LineTag, type ListKind, lineText, mergeOwn, type NameTagConfig, type OwnTags, ownAgainstGroup, ownKeys, sameOwn, splitName, tagKey, type TagKind, tagName, tagsIn, tagText } from "./names";

/*
 * Name tags (see names.ts): the fields that set them (an item's Details), and the full name as it
 * reads in ServiceM8. Which tags there are is up to each business (Price List settings).
 */

/** Value of "none" in the dropdowns (any other value is an option id or a line's value). */
const NONE = "__none";

/** A tag that isn't in the name: said the same way for every kind of tag (choice, line or include), on items and groups. */
const NOT_MENTIONED = { value: NONE, label: "Not mentioned" } as const;

/** A choice's options: not mentioned, or one of its options (as each reads in the name). */
const choiceOptions = (tag: ChoiceTag) => [NOT_MENTIONED, ...tag.options.map((o) => ({ value: o.id, label: o.text }))];

/**
 * A line's options: not mentioned, or the line as it reads under the name with each of its values
 * ("SPECIAL ORDER ITEM (3-7 BUSINESS DAYS)"), or just the line when it has none. `own`: an item's
 * value that isn't listed (typed in ServiceM8), shown the same way.
 */
function lineOptions(tag: LineTag, own: readonly string[] = []) {
    const values = tag.values.length ? [...tag.values, ...own] : ["", ...own.filter(Boolean)];
    return [NOT_MENTIONED, ...values.map((v) => ({ value: v, label: lineText(tag, v) }))];
}

/** An include tag's options: not mentioned, or in the name's includes or excludes, as it reads there ("Includes Keys"). */
const includeOptions = (tag: Pick<IncludeTag, "name">): { value: ListKind | typeof NONE; label: string }[] => [
    NOT_MENTIONED,
    { value: "includes", label: `Includes ${tag.name}` },
    { value: "excludes", label: `Excludes ${tag.name}` },
];

/** Lines with where each starts in the text (a stable key for each). */
function withOffsets(lines: string[]) {
    const out: { line: string; offset: number }[] = [];
    let offset = 0;
    for (const line of lines) {
        out.push({ line, offset });
        offset += line.length + 1;
    }
    return out;
}

/**
 * The full name as it reads in ServiceM8, with the parts the tags add picked out (they're set with
 * the fields, not typed).
 */
export function ServiceM8Name({ name, tags, only }: Readonly<{ name: string; tags: NameTagConfig; only?: "first" | "rest" }>) {
    const parts = splitName(name, tags);
    const [first = "", ...rest] = parts.base.split(/\r?\n/);
    const brackets = tagText(parts, tags);
    const lines = tags.lines.filter((t) => parts.lines[t.id] !== undefined);
    // Just the first line (the name and its tags in brackets), or just the lines under it (e.g. for a one-line list).
    if (only === "first") {
        return (
            <>
                {first}
                {brackets ? <span className="font-medium text-brand-ink"> ({brackets})</span> : null}
            </>
        );
    }
    if (only === "rest") {
        const under = [...rest.map((line) => ({ text: line, tag: false })), ...lines.map((t) => ({ text: lineText(t, parts.lines[t.id] ?? ""), tag: true }))];
        return (
            <>
                {under.map((u, i) => (
                    <span key={`${i}:${u.text}`} className={u.tag ? "font-medium text-warning" : undefined}>
                        {i > 0 ? " · " : null}
                        {u.text}
                    </span>
                ))}
            </>
        );
    }
    return (
        <>
            {first}
            {brackets ? <span className="font-medium text-brand-ink"> ({brackets})</span> : null}
            {withOffsets(rest).map(({ line, offset }) => (
                <span key={offset}>
                    {"\n"}
                    {line}
                </span>
            ))}
            {lines.map((t) => (
                <span key={t.id} className="font-medium text-warning">
                    {"\n"}
                    {lineText(t, parts.lines[t.id] ?? "")}
                </span>
            ))}
        </>
    );
}

/** The full name as ServiceM8 shows it, below the tags that make it up (nothing when it has no tags: it's the same as typed). */
export function NamePreview({ name, tags, label = "Full name in ServiceM8" }: Readonly<{ name: string; tags: NameTagConfig; label?: string }>) {
    const parts = splitName(name, tags);
    if (!tagText(parts, tags) && !Object.keys(parts.lines).length) return null;
    return (
        <div className="space-y-1">
            <div className="field-label">{label}</div>
            <div className="name-preview rounded-(--field-radius) bg-surface-2 px-(--field-px) py-(--field-py) whitespace-pre-line">
                <ServiceM8Name name={name} tags={tags} />
            </div>
        </div>
    );
}

/** A group setting an item's tags (its card above them says so: changed on the group, in its window). */
type SetBy = { id: string; name: string; tags: GroupTags; locksPrices: boolean };

/**
 * A tag, on an item or a group: a row, its name on the left in a narrow column (in ink when the tag is in the name, `mentioned`;
 * muted when it isn't, so the ones that say something stand out), its dropdown beside it, filling the row, then (an item in a
 * group) its Override switch at the end: the same for every kind of tag. Compact, so a business's many tags take little room.
 * An item overriding some of its group's: its box blue (as its price's panel when it overrides the group's price), the tags
 * following the group picked out in white (`plain`). Narrow: the dropdown under its name.
 */
function TagRow(
    props: Readonly<{
        label: string;
        mentioned: boolean;
        /**
         * Picked out from the box around it: on a blue box (an item overriding some of its group's tags), the ones following
         * the group, in white. Picked-out rows next to each other join into one block (`joinAbove`, `joinBelow`: rounded only at its ends).
         */
        plain?: boolean;
        joinAbove?: boolean;
        joinBelow?: boolean;
        aside?: ReactNode;
        children: ReactNode;
    }>,
) {
    const { label, mentioned, plain = false, aside, children } = props;
    return (
        <div
            className={clsx(
                // Spaced by its own padding (no gaps between rows), so picked-out rows meet.
                "-mx-1.5 grid items-center gap-x-3 gap-y-1 px-1.5 py-[3px] [&_.input]:py-1",
                // Its switch's column only when it has one: none, the dropdown runs to the edge.
                aside ? "grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[10rem_minmax(0,1fr)_auto]" : "grid-cols-1 sm:grid-cols-[10rem_minmax(0,1fr)]",
                plain && "bg-surface",
                plain && !props.joinAbove && "rounded-t-md",
                plain && !props.joinBelow && "rounded-b-md",
            )}
        >
            <span className={clsx("min-w-0 truncate", mentioned ? "text-ink" : "text-muted")} title={label}>
                {label}
            </span>
            {/* Narrow: its name and switch on one line, the dropdown under them. */}
            <div className={clsx("sm:order-none sm:col-span-1", aside && "order-last col-span-2")}>{children}</div>
            {aside ? <span className="justify-self-end">{aside}</span> : null}
        </div>
    );
}

/**
 * In a group: a card for it, the header of its Name tags box (as its Pricing tab has the group's card). Its name, the
 * tags it gives (as they read in a name), how many this item overrides, and Open group (its window, where they're changed).
 * Beside it (where its price's card has Override), showing all its tags or only those it overrides (`all`; none: no button,
 * while they're being edited).
 */
function GroupTagsCard(props: Readonly<{ group: SetBy; tags: NameTagConfig; overrides: readonly string[]; /** Show every tag to change (to save or cancel); none: they're showing, or it can't. */ onOverride?: () => void }>) {
    const { group, tags, overrides } = props;
    const overridden = overrides.filter((key) => tagName(key, tags)).length;
    // What it gives a name with none of its own.
    const gives = (groupTagsName("", group.tags, tags) ?? "").trim();
    return (
        <GroupCardHeader
            group={group}
            badge={overridden ? { text: `${overridden} ${overridden === 1 ? "tag" : "tags"} overridden`, title: "Tags this item sets itself, overriding the group's: the rest follow the group" } : undefined}
            summary={gives ? <ServiceM8Name name={gives} tags={tags} /> : "None: every tag is not mentioned."}
            opens="its name tags and all its items"
            actions={
                // Override (every tag shown to change: its save bar saves or cancels); already overriding some, "Edit overrides".
                props.onOverride ? (
                    <button
                        type="button"
                        className="btn-secondary"
                        title={overridden ? "Change the tags it overrides, or override more: every tag shown, then save or cancel" : "Set some of its tags its own way: every tag shown to change, then save or cancel"}
                        onClick={props.onOverride}
                    >
                        {overridden ? "Edit overrides" : "Override"}
                    </button>
                ) : undefined
            }
        />
    );
}

/**
 * A tag's options with its group's marked ("Includes Keys (group setting)"), for an item overriding its group's: what picking
 * it goes back to, seen without leaving the row. (Following the group, it's already showing the group's: no need to say so.)
 * `group`: its group's value (null: not mentioned); undefined: not in a group, left as they are.
 */
function markGroup<T extends string>(options: readonly { value: T; label: string }[], group: string | null | undefined) {
    if (group === undefined) return [...options];
    const key = group ?? NONE;
    return options.map((o) => (o.value === key ? { ...o, label: `${o.label} (group setting)` } : o));
}

/** A choice's dropdown: none, or one of its options. */
function ChoiceSelect(props: Readonly<{ tag: ChoiceTag; value: string | null | undefined; group?: string | null; disabled: boolean; onChange: (option: string | null) => Promise<unknown> }>) {
    const { tag } = props;
    return (
        <SelectField
            hideLabel
            label={tag.label}
            value={props.value ?? NONE}
            options={markGroup(choiceOptions(tag), props.group)}
            disabled={props.disabled}
            onSave={(v) => props.onChange(v === NONE ? null : v)}
        />
    );
}

/** A line's dropdown: no line, or one of its values (plus any others it might have that aren't listed: its name's, its group's). */
function LineSelect(props: Readonly<{ tag: LineTag; value: string | null | undefined; others: readonly (string | null | undefined)[]; group?: string | null; disabled: boolean; onChange: (value: string | null) => Promise<unknown> }>) {
    const { tag, value } = props;
    // Values that aren't listed (a line with none listed is just on or off: "" is the line alone): this one, and the others it might be.
    const own = [value, ...props.others].filter((v): v is string => typeof v === "string" && !tag.values.includes(v));
    return (
        <SelectField
            hideLabel
            label={tag.label}
            value={value ?? NONE}
            options={markGroup(lineOptions(tag, [...new Set(own)]), props.group)}
            disabled={props.disabled}
            onSave={(v) => props.onChange(v === NONE ? null : v)}
        />
    );
}

/** An include tag's dropdown: not mentioned, included or excluded (as it reads in the name). */
function IncludeSelect(props: Readonly<{ tag: IncludeTag; value: ListKind | null | undefined; group?: ListKind | null; disabled: boolean; onChange: (value: ListKind | null) => Promise<unknown> }>) {
    return (
        <SelectField
            hideLabel
            label={props.tag.name}
            value={props.value ?? NONE}
            options={markGroup(includeOptions(props.tag), props.group)}
            disabled={props.disabled}
            onSave={(v) => props.onChange(v === NONE ? null : (v as ListKind))}
        />
    );
}

type NameTagProps = Readonly<{
    name: string;
    tags: NameTagConfig;
    disabled: boolean;
    group?: SetBy;
    /** In a group: the tags it sets itself, as saved (overriding the group's). */
    own?: OwnTags;
    /**
     * Its tags being changed, not saved yet (kept by its window, like its pricing's draft): in a group, every tag it
     * sets itself; else, the tags changed. Null: none.
     */
    draft: OwnTags | null;
    onDraft: (draft: OwnTags | null) => void;
    /** Save the draft: in a group, the tags it sets itself (its name given them with `updateName`); else, its name with them. */
    onSave: (draft: OwnTags, updateName: boolean) => Promise<unknown>;
    /** Its name's warning (GroupNameNotice), along the bottom of its Name tags box (not while it's being edited), and Show's token. */
    fix?: ReactNode;
    fixToken?: number;
}>;

/** Own tags with one tag set (`value`), or taken out (`undefined`: back to what it follows). */
function withTag(own: OwnTags, kind: TagKind, id: string, value: string | null | undefined): OwnTags {
    const field = ({ choice: "choices", include: "includes", line: "lines" } as const)[kind];
    const record: Record<string, string | null> = { ...own[field] };
    if (value === undefined) delete record[id];
    else record[id] = value;
    return { ...own, [field]: record };
}

/**
 * An item's name tags, kept apart from its real fields: they only add wording to the name, and which
 * there are is set in Price List settings (linked). Edited as a draft, like its pricing, saved with the
 * bar under them. In a group: its card first (as on its Pricing tab), and only the tags it overrides
 * until Show all tags (on the card) shows the rest; every tag following the group
 * until something else is picked, when the item overrides it (tinted, its Override switch on; picking
 * the group's follows it again, and switching it off resets it to the group's). Saving can leave its
 * name as it is (Save only: a name to update), like a price. Nothing when the business has no tags.
 */
export function NameTags(props: NameTagProps) {
    const { name, tags, group, disabled } = props;
    // In a group: every tag shown, or only those it overrides (as it starts). Being edited, all of them.
    const [showAll, setShowAll] = useState(false);
    if (!hasTags(tags)) return null;
    const all = !group || showAll || props.draft !== null;
    const saved = props.own ?? {};
    // In a group: what it sets itself (the draft's, else as saved). On its own: the tags changed in the draft, over its name.
    const current = props.draft ?? (group ? saved : {});
    const shown: GroupTags = group ? itemTags(group.tags, tags, current) : (mergeOwn(tagsIn(name, tags), current) as GroupTags);
    const next = group ? (groupTagsName(name, group.tags, tags, current) ?? name) : applyTags(name, current, tags);
    const overridden = group ? ownKeys(current) : [];
    // A change: in a group, the group's isn't an override (it goes); on its own, its name's isn't a change. Undone: no draft.
    const change = (kind: TagKind, id: string, value: string | null | undefined) => {
        const base = group ? group.tags : tagsIn(name, tags);
        const edited = ownAgainstGroup(withTag(current, kind, id, value), base, tags);
        props.onDraft(sameOwn(edited, group ? saved : {}) ? null : edited);
        return Promise.resolve();
    };
    /** A tag in a group: overridden (tinted), and its Override switch (on: overridden; off resets it to the group's; never switched on). */
    const byGroup = (kind: TagKind, id: string) => {
        if (!group) return { overridden: false, aside: undefined };
        const key = tagKey(kind, id);
        const on = overridden.includes(key);
        return { overridden: on, aside: <ToggleField compact label="Override" checked={on} disabled={disabled || !on} ask={(to) => !to && void change(kind, id, undefined)} /> };
    };
    const live = group ? liveGroupTags(group.tags, tags) : undefined;
    const now = tagsIn(name, tags);
    // The tags shown: all, or (in a group, not showing all) only those it overrides.
    const shownTag = (kind: TagKind, id: string) => all || overridden.includes(tagKey(kind, id));
    const choices = tags.choices.filter((t) => shownTag("choice", t.id));
    const lines = tags.lines.filter((t) => shownTag("line", t.id));
    const includes = tags.includes.filter((i) => shownTag("include", i.id));
    // Overriding any: the whole box blue (like its price's panel when it overrides the group's), those following the group in white.
    const tinted = overridden.length > 0;
    // A row each, every kind alike: the tag's name, whether it's in the name and overridden, its Override switch and its dropdown.
    const row = (kind: TagKind, id: string, label: string, mentioned: boolean, field: (g: ReturnType<typeof byGroup>) => ReactNode) => {
        const g = byGroup(kind, id);
        return { key: tagKey(kind, id), label, mentioned, plain: tinted && !g.overridden, aside: g.aside, field: field(g) };
    };
    const rows = [
        ...choices.map((t) =>
            row("choice", t.id, t.label, (shown.choices[t.id] ?? null) !== null, (g) => (
                <ChoiceSelect tag={t} value={shown.choices[t.id]} group={live && g.overridden ? (live.choices[t.id] ?? null) : undefined} disabled={disabled} onChange={(v) => change("choice", t.id, v)} />
            )),
        ),
        ...lines.map((t) =>
            row("line", t.id, t.label, (shown.lines[t.id] ?? null) !== null, (g) => (
                <LineSelect
                    tag={t}
                    value={shown.lines[t.id]}
                    others={[now.lines[t.id], live?.lines[t.id]]}
                    group={live && g.overridden ? (live.lines[t.id] ?? null) : undefined}
                    disabled={disabled}
                    onChange={(v) => change("line", t.id, v)}
                />
            )),
        ),
        ...includes.map((i) =>
            row("include", i.id, i.name, (shown.includes[i.id] ?? null) !== null, (g) => (
                <IncludeSelect tag={i} value={shown.includes[i.id]} group={live && g.overridden ? (live.includes[i.id] ?? null) : undefined} disabled={disabled} onChange={(v) => change("include", i.id, v)} />
            )),
        ),
    ];
    // A draft: saved by its Save, so its fields don't say they're saved.
    return (
        <DraftFields>
            <NameTagsBox
                header={
                    group ? (
                        <GroupTagsCard group={group} tags={tags} overrides={overridden} onOverride={all || disabled ? undefined : () => setShowAll(true)} />
                    ) : undefined
                }
                overriding={overridden.length > 0}
                tinted={tinted}
                // Being edited: its save bar instead of its warning (which is about its tags as saved).
                fix={props.draft || (group && showAll) ? undefined : props.fix}
                fixToken={props.fixToken}
                // Being edited, or (in a group) its Override pressed: its save bar (nothing changed yet, Save just closes it).
                footer={
                    props.draft || (group && showAll) ? (
                        <TagsSave
                            grouped={group !== undefined}
                            changes={next !== name}
                            onSave={(updateName) =>
                                (props.draft ? props.onSave(current, updateName) : Promise.resolve()).then(() => {
                                    props.onDraft(null);
                                    // Saved (or cancelled, below): back to showing only what it overrides (the tags it follows are the group's business).
                                    setShowAll(false);
                                })
                            }
                            onCancel={() => {
                                props.onDraft(null);
                                setShowAll(false);
                            }}
                        />
                    ) : undefined
                }
            >
                {/* A row each, every kind alike. Showing only those it overrides: the same rows, just fewer. */}
                {rows.length ? (
                    <div>
                        {rows.map((r, i) => (
                            <TagRow key={r.key} label={r.label} mentioned={r.mentioned} plain={r.plain} joinAbove={rows[i - 1]?.plain} joinBelow={rows[i + 1]?.plain} aside={r.aside}>
                                {r.field}
                            </TagRow>
                        ))}
                    </div>
                ) : null}
                {/* Being edited: the name it gives; else the name as it is. */}
                <NamePreview name={props.draft ? next : name} tags={tags} label={props.draft && next !== name ? "Full name in ServiceM8, once updated" : undefined} />
            </NameTagsBox>
        </DraftFields>
    );
}

/**
 * Saving an item's name tags draft, like its pricing's: what saving does on the left, its buttons on the
 * right, worded as its pricing's are. With a new name: Save & update, and in a group Save only too (the name left as it
 * is, to update; on its own, its tags are its name, so there's nothing to save without it). No new name: Save name tags. And Cancel.
 */
function TagsSave(props: Readonly<{ grouped: boolean; changes: boolean; onSave: (updateName: boolean) => Promise<unknown>; onCancel: () => void }>) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const save = (updateName: boolean) => {
        setBusy(true);
        setError(null);
        props
            .onSave(updateName)
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    return (
        <DraftBar
            message={props.changes ? "The name changes to the full name above." : "The name doesn't change."}
            busy={busy}
            error={error}
            actions={
                <>
                    {props.grouped && props.changes ? (
                        <>
                            <button type="button" className="btn-primary" disabled={busy} title="Save the name tags and change the name in ServiceM8" onClick={() => save(true)}>
                                Save &amp; update
                            </button>
                            <button type="button" className="btn-secondary" disabled={busy} title="Save the name tags only: the name stays as it is, and shows as a name to update until it's updated" onClick={() => save(false)}>
                                Save only
                            </button>
                        </>
                    ) : (
                        <button type="button" className="btn-primary" disabled={busy} title={props.changes ? "Save the name tags: its name in ServiceM8 changes with them" : undefined} onClick={() => save(true)}>
                            {props.changes ? <>Save &amp; update</> : "Save name tags"}
                        </button>
                    )}
                    <button type="button" className="btn-secondary" disabled={busy} onClick={props.onCancel}>
                        Cancel
                    </button>
                </>
            }
        />
    );
}

const hasTags = (tags: NameTagConfig) => tags.choices.length > 0 || tags.lines.length > 0 || tags.includes.length > 0;

/**
 * The box name tags are set in (an item's or a group's), kept apart from the real fields, headed (above it) with where tags are set up;
 * any `header` (its group's card) along its top, and along its bottom any warning about them (`fix`: its name not
 * matching the tags it should have) or, being edited, its save bar (`footer`). `overriding`: the item overrides some
 * of its group's tags, so its header and border are blue (overriding: amber is for what needs attention), as its price's panel's
 * are when it overrides the group's price;
 * `tinted`: its body too, its save bar with it (the tags following the group and its full name in white).
 */
function NameTagsBox(
    props: Readonly<{
        intro?: string;
        header?: ReactNode;
        overriding?: boolean;
        tinted?: boolean;
        fix?: ReactNode;
        fixToken?: number;
        footer?: ReactNode;
        /** Its footer isn't a draft's save bar (e.g. a group's names to update): divided by a plain line, not tinted. */
        plainFooter?: boolean;
        children: ReactNode;
    }>,
) {
    const { intro, header, overriding, fix, fixToken, footer, children } = props;
    const id = useId();
    // Its heading above it, like a field's label ("Full name in ServiceM8"): "Name tags" on the left, what they are on the right (under it when narrow).
    const heading = (
        <SectionHeading
            id={id}
            label="Name tags"
            aside={
                <>
                    {intro ? `${intro} ` : null}Set up tags in <Link href={`/t/${meta.id}/settings#name-tags`}>Price List settings</Link>.
                </>
            }
        />
    );
    // Nothing in it (no tags yet): just the heading.
    if (!header && !children && !fix && !footer) return <div aria-labelledby={id}>{heading}</div>;
    return (
        // Its heading with it when a warning's Show brings it into view (FixHere.tsx).
        <div className="space-y-1" data-fix-scope>
            {heading}
            <section className={clsx("overflow-clip rounded-md border", (fix || footer) && "fix-card", props.tinted && "[&_.name-preview]:bg-surface", fix ? "border-warning/40" : overriding ? "border-override/30" : "border-line")} aria-labelledby={id}>
                {header ? <div className={clsx("rounded-t-md px-3 py-2.5", overriding ? "bg-override-header" : "bg-surface-2")}>{header}</div> : null}
                {/* Its tags and full name, under a line from its header; nothing in it (a group item overriding none, collapsed): not there at all. */}
                <div className={clsx("space-y-3.5 p-3 empty:hidden", header && "border-t", overriding ? "border-override/30" : "border-line", props.tinted && "bg-override-tint")}>{children}</div>
                {fix ? (
                    <FixHere attached token={fixToken}>
                        {fix}
                    </FixHere>
                ) : null}
                {/* On a blue box, blue too, divided by a blue line (a green tint over it reads muddy). */}
                {/* Being edited: its save bar is where its name warning's Show brings you (FixHere.tsx: the standard). */}
                {footer ? (
                    <FixHere attached token={fixToken} className="overflow-visible rounded-none">
                        <div
                            className={clsx(
                                "rounded-b-md border-t px-3 py-2",
                                props.plainFooter ? "border-line" : props.tinted ? "border-override/30 bg-override-tint" : "border-brand-500/30 bg-brand-500/5",
                            )}
                        >
                            {footer}
                        </div>
                    </FixHere>
                ) : null}
            </section>
        </div>
    );
}

/** A group's tag as a field's value: the value it sets, or not mentioned (none listed, or null). */
const groupValue = (record: Record<string, string | null>, id: string) => record[id] ?? NONE;

/** What a group sets, with one tag changed to a field's value (not mentioned: left out, as it is by default). */
function withValue<T extends string>(record: Record<string, T | null>, id: string, value: string): Record<string, T | null> {
    const next = { ...record };
    if (value === NONE) delete next[id];
    else next[id] = value as T;
    return next;
}

/**
 * The name tags a group sets for its items, laid out as an item's are (a row each, every kind a
 * dropdown): every tag, not mentioned by default (its label muted, so the ones it mentions stand out;
 * an item can override one). Under them, an example name (`exampleBase` with the group's tags).
 */
export function GroupTagFields(
    props: Readonly<{
        tags: NameTagConfig;
        set: GroupTags;
        exampleBase: string;
        disabled: boolean;
        onChange: (next: GroupTags) => Promise<unknown>;
        /** In the card under its example: the names they change (GroupWindow.tsx), as a group's Pricing card has its prices. */
        after?: ReactNode;
        /** Along its bottom: its save bar (a draft), or (`plainFooter`) its names to update. */
        footer?: ReactNode;
        plainFooter?: boolean;
    }>,
) {
    const { tags, set, disabled } = props;
    if (!hasTags(tags)) return <NameTagsBox intro="There are no name tags yet.">{null}</NameTagsBox>;
    const change = (next: Partial<GroupTags>) => props.onChange({ ...set, ...next });
    // A draft (its group's): saved by its Save, so its fields don't say they're saved.
    return (
        <DraftFields>
            <NameTagsBox intro="Its items get these tags (each can override one)." footer={props.footer} plainFooter={props.plainFooter}>
                {/* A row each, every kind alike (as an item's). */}
                <div>
                    {tags.choices.map((t) => (
                        <TagRow key={t.id} label={t.label} mentioned={groupValue(set.choices, t.id) !== NONE}>
                            <ChoiceSelect tag={t} value={set.choices[t.id]} disabled={disabled} onChange={(v) => change({ choices: withValue(set.choices, t.id, v ?? NONE) })} />
                        </TagRow>
                    ))}
                    {tags.lines.map((t) => (
                        <TagRow key={t.id} label={t.label} mentioned={groupValue(set.lines, t.id) !== NONE}>
                            <LineSelect tag={t} value={set.lines[t.id]} others={[]} disabled={disabled} onChange={(v) => change({ lines: withValue(set.lines, t.id, v ?? NONE) })} />
                        </TagRow>
                    ))}
                    {tags.includes.map((i) => (
                        <TagRow key={i.id} label={i.name} mentioned={groupValue(set.includes, i.id) !== NONE}>
                            <IncludeSelect tag={i} value={set.includes[i.id]} disabled={disabled} onChange={(v) => change({ includes: withValue(set.includes, i.id, v ?? NONE) })} />
                        </TagRow>
                    ))}
                </div>
                <NamePreview name={groupTagsName(props.exampleBase, set, tags) ?? props.exampleBase} tags={tags} label="Example" />
                {props.after}
            </NameTagsBox>
        </DraftFields>
    );
}

const DIFF_STYLE: Record<DiffPart["change"], string | undefined> = {
    same: undefined,
    removed: "rounded-sm bg-danger-soft text-danger line-through box-decoration-clone",
    added: "rounded-sm bg-brand-500/15 text-ink box-decoration-clone",
};

/** One line of a name's change, its changes marked (removed struck through, added highlighted). */
export function DiffLine({ parts }: Readonly<{ parts: readonly DiffPart[] }>) {
    return (
        <>
            {parts.map((part, i) => (
                <span key={`${i}:${part.change}`} className={DIFF_STYLE[part.change]}>
                    {part.text}
                </span>
            ))}
        </>
    );
}

/**
 * Its name reads differently from the tags it should have (its group's, and those it sets itself: changed
 * on the group, saved without updating its name, or its name changed in ServiceM8), like a price to
 * update: the name it would have, as the full name shows it, then why, with Update name or Keep (the
 * tags that differ become its own, overriding the group's), as the person clicking. Along the bottom of its Name tags box.
 */
export function GroupNameNotice({ data, item }: Readonly<{ data: PriceListData; item: MaterialRow }>) {
    const saver = usePriceListSaver();
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const check = groupNameCheck(data, item);
    if (!check) return null;
    const run = (fn: () => Promise<unknown>) => {
        setBusy(true);
        setError(null);
        fn()
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    // Keeping its name: the tags that read differently in it become its own.
    const own = data.itemPricing[item.uuid]?.tagOverrides;
    const differing = differingTags(item.name, check.group.tags, data.nameTags, own);
    const which = differing.map((key) => tagName(key, data.nameTags)).filter(Boolean);
    return (
        <div className="space-y-2 border-t border-warning/30 bg-warning-soft px-3 py-2.5 text-warning-ink">
            <p className="m-0 flex items-center gap-2">
                <TriangleAlert className="size-4 shrink-0" />
                <strong>Name to update:</strong>
            </p>
            <div className="rounded-(--field-radius) bg-surface px-(--field-px) py-(--field-py) whitespace-pre-line text-ink">
                <ServiceM8Name name={check.name} tags={data.nameTags} />
            </div>
            {/* Why on the left, its buttons on the right (under it only when narrow), as in its price's warning. */}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 sm:flex-nowrap">
                <p className="m-0 min-w-0 flex-1">The name doesn&apos;t match {ownKeys(own).length ? "its name tags" : `the ${check.group.name} group's tags`}.</p>
                {can(data, "items.edit") ? (
                    <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                        <button type="button" className="btn-primary" disabled={busy} onClick={() => run(() => saver.updateNameToGroup(item.uuid))}>
                            Update name
                        </button>
                        <button
                            type="button"
                            className="btn-secondary"
                            disabled={busy || !differing.length}
                            title={`Keep its name as it is: ${which.length ? which.join(", ") : "those tags"} ${which.length === 1 ? "becomes its own" : "become its own"}, overriding the group's`}
                            onClick={() => run(() => saver.keepName(item.uuid))}
                        >
                            Keep (override)
                        </button>
                    </div>
                ) : null}
            </div>
            <ErrorNotice error={error} />
        </div>
    );
}
